import { Button, Card, CardContent, Label, Select, Spinner } from '@crm/ui';
import { ArrowLeft, CheckCircle2, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { CONTACT_LISTS_ROUTES } from '@/modules/contact-lists/api';
import { type ImportRow, useImportContacts } from '../api';

/** As colunas que o sistema entende, na ordem em que a tela pergunta. */
const CAMPOS = [
  { key: 'name', label: 'Nome', obrigatorio: true },
  { key: 'phone', label: 'WhatsApp', obrigatorio: true },
  { key: 'whatsappName', label: 'Nome no WhatsApp', obrigatorio: false },
  { key: 'birthMonth', label: 'Mes de nascimento', obrigatorio: false },
  { key: 'birthYear', label: 'Ano de nascimento', obrigatorio: false },
  { key: 'gender', label: 'Sexo', obrigatorio: false },
] as const;

type CampoKey = (typeof CAMPOS)[number]['key'];

/** Palpite do que cada coluna do arquivo significa, pelo nome do cabecalho. */
const APELIDOS: Record<CampoKey, string[]> = {
  name: ['nome', 'name', 'cliente'],
  phone: ['whatsapp', 'telefone', 'celular', 'phone', 'fone'],
  whatsappName: ['nome_whatsapp', 'nomewhatsapp', 'nome whatsapp'],
  birthMonth: ['mes_nascimento', 'mes', 'mesnascimento'],
  birthYear: ['ano_nascimento', 'ano', 'anonascimento'],
  gender: ['sexo', 'genero', 'gender'],
};

/**
 * Le o CSV no proprio navegador. E o mesmo trabalho que a previa exige - e
 * evita mandar o arquivo inteiro para o servidor antes de alguem conferir se
 * as colunas estao no lugar certo.
 */
function lerCsv(texto: string): { cabecalho: string[]; linhas: string[][] } {
  const linhas = texto
    .split(/\r?\n/)
    .map((linha) => linha.trim())
    .filter(Boolean);

  if (linhas.length === 0) return { cabecalho: [], linhas: [] };

  const separador =
    (linhas[0]?.match(/;/g)?.length ?? 0) > (linhas[0]?.match(/,/g)?.length ?? 0) ? ';' : ',';

  const partir = (linha: string) =>
    linha.split(separador).map((celula) => celula.trim().replace(/^"|"$/g, ''));

  return { cabecalho: partir(linhas[0] ?? ''), linhas: linhas.slice(1).map(partir) };
}

function adivinha(cabecalho: string[]): Record<CampoKey, number> {
  const normalizado = cabecalho.map((coluna) => coluna.toLowerCase().trim());
  const mapa = {} as Record<CampoKey, number>;

  for (const campo of CAMPOS) {
    mapa[campo.key] = normalizado.findIndex((coluna) =>
      APELIDOS[campo.key].some((apelido) => coluna === apelido || coluna.includes(apelido)),
    );
  }

  return mapa;
}

export function ContactsImportPage() {
  useDocumentTitle('Importar contatos');

  const importar = useImportContacts();

  const [fileName, setFileName] = useState('');
  const [cabecalho, setCabecalho] = useState<string[]>([]);
  const [linhas, setLinhas] = useState<string[][]>([]);
  const [mapa, setMapa] = useState<Record<CampoKey, number>>({} as Record<CampoKey, number>);

  async function escolheArquivo(arquivo: File) {
    const texto = await arquivo.text();
    const { cabecalho: colunas, linhas: dados } = lerCsv(texto);

    setFileName(arquivo.name);
    setCabecalho(colunas);
    setLinhas(dados);
    setMapa(adivinha(colunas));
    importar.reset();
  }

  function envia() {
    const rows: ImportRow[] = linhas.map((linha) => {
      const registro: ImportRow = {};

      for (const campo of CAMPOS) {
        const indice = mapa[campo.key];
        if (indice >= 0) registro[campo.key] = linha[indice] ?? '';
      }

      return registro;
    });

    importar.mutate({ fileName, rows });
  }

  const faltaObrigatorio = CAMPOS.some((campo) => campo.obrigatorio && (mapa[campo.key] ?? -1) < 0);

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={CONTACT_LISTS_ROUTES.LIST}
          className="-ml-1 mb-1 inline-flex min-h-10 items-center gap-1.5 px-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Listas de contatos
        </Link>

        <PageHeader
          icon={<Upload />}
          title="Importar contatos"
          description="Uma planilha em CSV com nome e WhatsApp vira cadastro de cliente."
        />
      </div>

      <Card variant="flush">
        <CardContent className="py-5">
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-card border border-dashed border-border-strong px-4 py-8 text-center transition-colors hover:bg-muted/50">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <span className="font-medium">{fileName || 'Escolher arquivo CSV'}</span>
            <span className="text-sm text-muted-foreground">
              Primeira linha com o nome das colunas
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(input) => {
                const arquivo = input.target.files?.[0];
                if (arquivo) void escolheArquivo(arquivo);
              }}
            />
          </label>
        </CardContent>
      </Card>

      {importar.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(importar.error)}</p>
      ) : null}

      {importar.data ? (
        <Card variant="flush">
          <CardContent className="space-y-3 py-5">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              <p className="font-medium">Importacao concluida</p>
            </div>

            <p className="text-sm">
              {importar.data.created} criados, {importar.data.updated} atualizados,{' '}
              {importar.data.skipped} recusados de {importar.data.total} linhas.
            </p>

            {importar.data.errors.length ? (
              <ul className="space-y-1 rounded-card bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                {importar.data.errors.slice(0, 10).map((erro) => (
                  <li key={erro.line}>
                    Linha {erro.line}: {erro.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {linhas.length > 0 && !importar.data ? (
        <>
          <Card variant="flush">
            <CardContent className="space-y-4 py-5">
              <h2 className="font-medium">De qual coluna sai cada informacao</h2>

              <div className="grid gap-3 sm:grid-cols-2">
                {CAMPOS.map((campo) => (
                  <div key={campo.key} className="space-y-2">
                    <Label htmlFor={`campo-${campo.key}`}>
                      {campo.label}
                      {campo.obrigatorio ? ' *' : ''}
                    </Label>
                    <Select
                      id={`campo-${campo.key}`}
                      value={String(mapa[campo.key] ?? -1)}
                      onChange={(input) =>
                        setMapa({ ...mapa, [campo.key]: Number(input.target.value) })
                      }
                    >
                      <option value="-1">Nao importar</option>
                      {cabecalho.map((coluna, indice) => (
                        <option key={coluna} value={indice}>
                          {coluna}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card variant="flush">
            <CardContent className="p-0">
              <h2 className="border-b border-border py-3 font-medium">
                Primeiras linhas ({linhas.length} no total)
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-muted-foreground">
                    <tr>
                      {CAMPOS.map((campo) => (
                        <th key={campo.key} className="whitespace-nowrap px-4 py-2 font-medium">
                          {campo.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {linhas.slice(0, 10).map((linha, indice) => (
                      // A ordem da planilha e a identidade da linha: duas
                      // pessoas com o mesmo nome sao duas linhas diferentes.
                      // biome-ignore lint/suspicious/noArrayIndexKey: a posicao e o dado
                      <tr key={indice}>
                        {CAMPOS.map((campo) => (
                          <td key={campo.key} className="whitespace-nowrap px-4 py-2">
                            {mapa[campo.key] >= 0 ? (linha[mapa[campo.key]] ?? '') : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-3">
            {faltaObrigatorio ? (
              <p className="text-sm text-muted-foreground">
                Escolha as colunas de nome e WhatsApp.
              </p>
            ) : null}
            <Button onClick={envia} disabled={faltaObrigatorio || importar.isPending}>
              {importar.isPending ? <Spinner /> : null}
              Importar {linhas.length} contatos
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
